/**
 * @file MemberTasksPage.jsx
 * @location src/pages/MemberTasksPage.jsx
 * @role 업무탭(activeNav==='tasks')의 팀원(비매니저) 콘텐츠
 *
 * 새 축(매니저 1명 전용): 팀원은 할당받은 업무 안에서 핸드셰이크만(열람→완료). 발신·전체·토글 없음.
 * 매니저와 동일한 표 틀(TeamTaskTable) — 단 상태열 대신 체크열, 담당-열 제거(본인뿐).
 * 안 읽음은 표에서 제외 → 최상단 우체통(MemberInbox)에서 확인 후 표로.
 * 미래 예정(ondate, due>today)은 숨기지 않고 예정 업무 섹션으로(미리 열람 가능).
 * 하단 = 담당 루틴 카드(본인 발생분 체크).
 */
import { useMemo } from 'react';
import { useApp } from '../store/AppContext';
import { deriveMemberView } from '../utils/memberView';
import { getVisibleMemberIds, filterVisibleTasks } from '../utils/visibility';
import { getAttentionBuckets } from '../utils/managerAttention';
import { useCompleteConfirm, completeConfirmMsg } from '../hooks/useCompleteConfirm';
import { ConfirmModal } from '../components/ui/sharedui/ConfirmModal';
import { getTodayStr } from '../utils/dateUtils';
import { TeamTaskTable } from '../components/team/TeamTaskTable';
import { MemberRoutineCards } from '../components/tasks/MemberRoutineCards';
import { TasksFoot } from '../components/tasks/TasksFoot';
import { MemberInbox } from '../components/mypage/MemberInbox';
import { MemberDailySummary } from '../components/mypage/MemberDailySummary';
import { Icon } from '../components/ui/Icon';

// 팀원 표 컬럼 — 상태열→체크열, 담당-(본인뿐) 열 제거
const MEMBER_COLS = ['check', 'title', 'priority', 'due'];

export function MemberTasksPage({ onToast }) {
  const { S } = useApp();
  const templates = S.taskTemplates ?? [];
  const today = getTodayStr();

  // 가시성 — 팀원=본인만. releasedToMember 숨김 폐지 → 미래 예정도 받는다(예정 업무 섹션).
  const all = useMemo(
    () => filterVisibleTasks(S.tasks, getVisibleMemberIds(S)),
    [S.tasks, S.members, S.uid],
  );
  const buckets = useMemo(() => getAttentionBuckets(all, S.uid), [all, S.uid]);
  const { inboxUnread, tableTasks, todayCount, futureCount } = useMemo(
    () => deriveMemberView(all, buckets, today),
    [all, buckets, today],
  );
  const { requestToggle, handleToggle, confirmTask, onConfirm, onCancel } = useCompleteConfirm(onToast);

  return (
    <div className="content cockpit-wide">
      {/* 하루 첫 진입 1회 요약 */}
      <MemberDailySummary todayCount={todayCount} futureCount={futureCount} today={today} />

      {/* 업무도착 — 오늘 도래 안 읽음(확인하면 표로) */}
      <MemberInbox unread={inboxUnread} />

      <TeamTaskTable
        filteredTasks={tableTasks}
        buckets={buckets}
        handleToggle={requestToggle}
        onToast={onToast}
        cols={MEMBER_COLS}
        hideUnackAttn
        centerNotice={inboxUnread.length > 0
          ? <span className="filter-notice"><Icon name="arrow-down" size={20} strokeWidth={2.4} /> 확인 후 업무를 시작해 주세요</span>
          : null}
      />

      <TasksFoot />

      {/* 하단 전폭 — 담당 루틴업무(읽기 + 본인 발생분 체크) */}
      <div className="routine-bottom">
        <div className="recur-section-head">
          <div className="recur-section-title"><Icon name="reset" size={16} /> 담당 루틴 업무</div>
        </div>
        <MemberRoutineCards templates={templates} handleToggle={handleToggle} />
      </div>
      {confirmTask && (
        <ConfirmModal
          msg={completeConfirmMsg(confirmTask, today)}
          okLabel="완료로 처리"
          okClass="btnp"
          onOk={onConfirm}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}
